import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getUsersApiClient } from "@shared/apis/sidekick/api-factories";
import { toast } from "react-toastify";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { UpdateCommunicationPreferenceRequest } from "@shared/apis/sidekick/generated";
import { GET_USER_PROFILE_QUERY_KEY } from "./useGetUserProfile";
import { useAuthStore } from "@store/auth.store";

type Props = {
  onSuccess?: (params: Params) => void;
  onError?: () => void;
  onSettled?: (params: Params) => void;
};

type Params = {
  type: UpdateCommunicationPreferenceRequest["type"];
  enabled: boolean;
};

export const useUpdateUserCommunicationPreferences = (props?: Props) => {
  const { onSuccess, onError, onSettled } = props || {};
  const fileApi = getUsersApiClient();
  const queryClient = useQueryClient();
  const setPartialUserData = useAuthStore((state) => state.setPartialUserData);
  const user = useAuthStore((state) => state.user);
  const mutation = useMutation({
    mutationFn: async ({ type, enabled }: Params) => {
      return fileApi.userControllerUpdateCommunicationPreference({
        updateCommunicationPreferenceRequest: {
          type,
          enabled,
        },
      });
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: [GET_USER_PROFILE_QUERY_KEY],
      });
      setPartialUserData({
        communicationPreferences: {
          ...user.communicationPreferences,
          [params.type]: {
            ...user.communicationPreferences[params.type],
            isEnabled: params.enabled,
          },
        },
      });
      onSuccess?.(params);
    },
    onError: async (error) => {
      onError?.();
      console.error("Failed to update user communication preferences", error);
      const apiError = await apiResponseErrorHandler(error);
      if (apiError.status >= 400 && apiError.status < 500) {
        toast.error(apiError.errors[0], {
          toastId: "update-user-communication-preferences-error",
        });
        return;
      }
      toast.error("Error updating user communication preferences", {
        toastId: "update-user-communication-preferences-error",
      });
    },
    onSettled: (_data, _error, params) => {
      onSettled?.(params);
    },
  });

  return mutation;
};
